import * as React from 'react'
import theme from 'theme'
import styled from 'styled-components'
import { ReactComponent as LinkArrow } from 'assets/link-arrow.svg'
import EecTracking from 'theme/atoms/EecTracking'
import Link from 'theme/atoms/Link'
import useLazyImgSrc from 'hooks/useLazyImgSrc'
import * as t from './types'
import Markdown from 'theme/atoms/Markdown'

export default function BrandroomImageTeaser(props: t.Props) {
  const {
    gridArea,
    link,
    linkLabel,
    title,
    left,
    eecTracking,
    context
  } = props
  const [ref, imgUrl] = useLazyImgSrc<HTMLImageElement>(context.optImg, 100)

  return (
    <EecTracking config={eecTracking} gridArea={gridArea}>
      <div>
        <Wrapper
          $imgSrc={imgUrl}
          $left={left}
          to={link ? link : ''}
          ref={ref}
          className="ImageTeaser"
          data-cy-ctx={'organisms/BrandroomImageTeaser'}
          aria-label={linkLabel ? linkLabel : 'navigate to content'}
          tabIndex={link ? 0 : -1}
        >
          <TeaserDescription $left={left} className="teaser-description">
            {title && (
              <h3 data-cy-state={'show-title'} className="title">
                {title}
              </h3>
            )}
            <Markdown html={context.descriptionTextHtml} />
            {link && (
              <div data-cy-state={'show-link'}>
                <span>
                  {linkLabel} <LinkArrow />
                </span>
              </div>
            )}
          </TeaserDescription>
          <div className="teaser-image"></div>
        </Wrapper>
      </div>
    </EecTracking>
  )
}

const Wrapper = styled(Link)<{ $imgSrc: string; $left: boolean }>`
  display: block;
  position: relative;
  width: 100%;
  height: fit-content;

  @media (min-width: ${theme.ms.LG}px) {
    padding-top: ${theme.spacing('l')};
  }
  > .teaser-image {
    background-image: url('${(p) => p.$imgSrc}');
    display: block;
    background-size: cover;
    background-position: center center;
    width: 100%;
    height: 18.75rem;
    @media (min-width: ${theme.ms.XS}px) {
      background-position: ${(p) => (p.$left ? 'right center' : 'left center')};
    }
    @media (min-width: ${theme.ms.SM}px) {
      background-position: center center;
    }
    @media (min-width: ${theme.ms.MD}px) {
      height: 21.25rem;
    }
    @media (min-width: ${theme.ms.LG}px) {
      height: 25rem;
    }
  }
`
const TeaserDescription = styled.div<{ $left: boolean }>`
  position: relative;
  border-radius: ${(p) =>
    p.$left ? '1.25rem 1.25rem 0rem 1.25rem' : '1.25rem 1.25rem 1.25rem 0rem'};
  padding: ${theme.spacing('m')};
  background: ${theme.colors.white};
  left: ${(p) => (p.$left ? `${theme.spacing('s')}` : 'initial')};
  right: ${(p) => (p.$left ? 'initial' : `${theme.spacing('s')}`)};
  margin-left: ${(p) => (p.$left ? 'initial' : `${theme.spacing('l')}`)};
  margin-right: ${(p) => (p.$left ? `${theme.spacing('l')}` : 'initial')};
  margin-bottom: -${theme.spacing('l')};

  @media (min-width: ${theme.ms.MD}px) {
    max-width: 28.125rem;
    border-radius: ${(p) =>
      p.$left
        ? '1.875rem 1.875rem 0rem 1.875rem'
        : '1.875rem 1.875rem 1.875rem 0rem'};
    margin-bottom: -${theme.spacing('xl')};

    padding: ${theme.spacing('l')};
    left: ${(p) => (p.$left ? `${theme.spacing('m')}` : 'initial')};
    right: ${(p) => (p.$left ? 'initial' : `${theme.spacing('m')}`)};
    margin-left: ${(p) => (p.$left ? 'initial' : `auto`)};
    margin-right: ${(p) => (p.$left ? `auto` : 'initial')};
  }

  @media (min-width: ${theme.ms.LG}px) {
    position: absolute;
    padding: ${theme.spacing('xl')};
    left: ${(p) => (p.$left ? `${theme.spacing('l')}` : 'initial')};
    right: ${(p) => (p.$left ? 'initial' : `${theme.spacing('l')}`)};
    max-width: 40%;
    margin-bottom: 0;
    top: 0;
  }

  > .title {
    ${theme.ty('rc-base', null, 'rc-2xl')}
    margin-bottom: ${theme.spacing('xxs')};
    @media (min-width: ${theme.ms.MD}px) {
      margin-bottom: ${theme.spacing('xs')};
    }
  }
    > .Markdown {
      > p {
        ${theme.ty('rc-s', null, 'rc-base')}
        display: -webkit-box;
        -webkit-box-orient: vertical;
        overflow: hidden;
        text-overflow: ellipsis;
        -webkit-line-clamp: 6;
        @media (min-width: ${theme.ms.MD}px) {
          margin: 0;
          margin-bottom: ${theme.spacing('xs')};
        }
        @media (min-width: ${theme.ms.LG}px) {
          -webkit-line-clamp: 10;
        }
      }
    }
  > div > span {
    ${theme.ty('rc-s', 'bold', 'rc-base')}
    padding-top: ${theme.spacing('xs')};
    color: ${theme.colors.b0};

    @media (min-width: ${theme.ms.LG}px) {
      padding-top: ${theme.spacing('m')};
    }

    > svg {
      margin-bottom: -0.1875rem;

      #Path-5,
      #Path-6 {
      }
      g {
        filter: none;
      }
    }
  }
`